import { createAsyncThunk } from '@reduxjs/toolkit';
import ReconciliationsResource from 'api/service/ReconciliationsResource';
import QuotesResource from 'api/service/QuotesResource';
import OrganizationsResource from 'api/service/OrganizationsResource';
import FilesServiceResource from 'api/service/FilesResource';
import { downloadFile } from 'utils/download';

export const listReconciliationQuotes = createAsyncThunk(
  'reconciliation/listReconciliationQuotes',
  async (initialData) => {
    const reconciliationsResource = new ReconciliationsResource();
    const { statuses, quoteSort, filterString, organizations } = initialData;

    const quoteSortString = Object.values(quoteSort).join(' ');
    const defaultStatuses = 'ApprovedAwaitingPayment,ApprovedPaymentProgressing,ApprovedPaymentReceived';
    const statusesParams = statuses && statuses.length > 0 ? statuses.join(',') : defaultStatuses;
    const orgParams = organizations.map((item) => item.id).join(',');

    const query = {
      size: 100,
      includes: 'items',
      order_by: quoteSortString,
      status: statusesParams,
      ...(orgParams && { accounts: orgParams }),
      ...(filterString && { search: filterString }),
    };

    const { status, data } = await reconciliationsResource.list(query);
    if (status === 200) {
      return { data, fromFilter: statuses.length > 0 };
    }
    return [];
  }
);

export const listQuoteAttachments = createAsyncThunk('reconciliation/listQuoteAttachments', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quoteId, itemId, s = -1 } = initialData;
  const params = { item: itemId, s };

  const { status, data } = await quotesResource.quote(quoteId).attachments().list(params);
  if (status === 200) {
    return data;
  }
  return [];
});

export const uploadQuoteAttachments = createAsyncThunk('reconciliation/uploadQuoteAttachments', async (initialData) => {
  const quotesResource = new QuotesResource();
  const filesResource = new FilesServiceResource();

  const { quoteId, files, itemId } = initialData;

  if (files.length === 0) return;

  // Use map to create an array of promises for Promise.all
  const uploadPromises = Array.from(files).map(async (file) => {
    const { status, data } = await quotesResource.quote(quoteId).attachments().upload(file, itemId);
    if (status === 200) {
      const { upload_url } = data;

      const { status: uploadStatus } = await quotesResource.uploadAttachment(upload_url, file);

      if (uploadStatus === 201) {
        const { status: fileStatus, data: fileData } = await filesResource.file(data.oid).refresh();
        if (fileStatus === 200) return fileData;
      }
    }
  });

  // Wait for all uploads to complete
  const results = await Promise.all(uploadPromises);

  return results; // Returns an array of successful responses
});

export const listStatusesCount = createAsyncThunk('reconciliation/listStatusesCount', async (initialData) => {
  const reconciliationsResource = new ReconciliationsResource();
  const { organizations } = initialData;
  const orgParams = organizations.map((item) => item.id).join(',');
  const { status, data } = await reconciliationsResource.statusCount({ accounts: orgParams });
  if (status === 200) {
    return data;
  }
  return [];
});

export const downloadQuoteAttachment = createAsyncThunk(
  'reconciliation/downloadQuoteAttachment',
  async (initialData) => {
    const quotesResource = new QuotesResource();
    const { quoteId, attachmentId, attachmentName } = initialData;
    const { status, data } = await quotesResource.quote(quoteId).attachments().download(attachmentId);
    if (status === 200) {
      downloadFile(data, attachmentName);
    }
  }
);

export const removeQuoteAttachment = createAsyncThunk('reconciliation/removeQuoteAttachment', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quoteId, attachmentId } = initialData;
  const { status } = await quotesResource.quote(quoteId).attachments().remove(attachmentId);
  if (status === 202) {
    return attachmentId;
  }
});

export const listOrganizations = createAsyncThunk('reconciliation/listOrganizations', async () => {
  const organizationsResource = new OrganizationsResource();
  const { status, data } = await organizationsResource.list();
  if (status === 200) {
    return data;
  }
  return [];
});

export const listPropertyQuotes = createAsyncThunk(
  'reconciliation/listPropertyQuotes',
  async (initialData, { rejectWithValue, getState }) => {
    const quotesResource = new QuotesResource();
    const { quoteFilters } = getState().reconciliation;
    const { propertyId } = initialData;

    //use try catch to transform the error payload into a specific shape
    try {
      const { status, data } = await quotesResource.listPropertyQuotes(propertyId, quoteFilters);

      if (status === 200) {
        return { propertyId, data };
      } else {
        return rejectWithValue({ propertyId, error: 'Unexpected status', data: [] });
      }
    } catch (error) {
      return rejectWithValue({ propertyId, error: error.message, data: [] });
    }
  }
);

export const updateReconciliationQuote = createAsyncThunk(
  'reconciliation/updateReconciliationQuote',
  async (initialData, { rejectWithValue }) => {
    const reconciliationsResource = new ReconciliationsResource();
    const { id, payload } = initialData;
    const { status, data } = await reconciliationsResource.reconciliation(id).update(payload);
    if (status === 200) {
      return data;
    }
    return rejectWithValue('Updating of Reconciliation failed.');
  }
);
